/* eslint-disable max-lines */
import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { FC, useState } from "react";
import toast from "react-hot-toast";
import { v4 as uuid } from "uuid";
import {
  CreditRedeemableScope,
  REDEEMABLE_SCOPE_LABELS
} from "../../interfaces/wallet/Wallet.types";
import { issueCredits } from "../../services/Wallet.service";
import { resolveError } from "../../utils/apiErrorMessage";
import Button from "../ui/Button";
import DayPicker from "../ui/datepicker/day/DayPicker";
import IconBtn from "../ui/IconBtn";
import Modal from "../ui/Modal";

interface Props {
  defaultEmail?: string;
  onSuccess?: () => void;
}

// The chosen day is the last day the credits still work, so the deadline sits at the end of it in the
// app's zone rather than at midnight UTC — otherwise a grant "until 31 December" lapses on the 30th here.
const toExpiryInstant = (day: Date) =>
  dayjs.tz(dayjs(day).format("YYYY-MM-DD"), "Europe/Belgrade")
    .endOf("day")
    .toISOString();

const defaultExpiry = () => dayjs().add(12, "month").toDate();

// Loose on purpose: it only catches the typo that would open a new wallet nobody reads.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const isEmailValid = (value: string) => EMAIL_REGEX.test(value.trim());

const IssueCreditsModal: FC<Props> = ({ defaultEmail, onSuccess }) => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [amount, setAmount] = useState("");
  const [scope, setScope] = useState<CreditRedeemableScope>(
    CreditRedeemableScope.Any
  );
  const [isServiceRecovery, setIsServiceRecovery] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | undefined>(defaultExpiry);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // One id per opened form, sent again on every retry of it: a submit that timed out but went through is
  // then answered with the grant it already made instead of granting twice.
  const [requestId, setRequestId] = useState(uuid);

  const reset = () => {
    setEmail(defaultEmail ?? "");
    setAmount("");
    setScope(CreditRedeemableScope.Any);
    setIsServiceRecovery(false);
    setExpiresAt(defaultExpiry());
    setReason("");
    setSubmitting(false);
    setRequestId(uuid());
  };

  // Seed on open, not at mount: on the wallet page the email arrives after the first render.
  const handleOpen = () => {
    reset();
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    reset();
  };

  const handleSubmit = async () => {
    const parsed = Number(amount);

    if (!isEmailValid(email)) {
      toast.error("Shkruani një email të plotë, p.sh. emri@shembull.com.");
      return;
    }
    if (!amount.trim() || Number.isNaN(parsed) || parsed <= 0) {
      toast.error("Shuma duhet të jetë më e madhe se zero.");
      return;
    }
    if (!reason.trim()) {
      toast.error("Arsyeja është e detyrueshme.");
      return;
    }

    setSubmitting(true);
    try {
      await issueCredits({
        email: email.trim(),
        amount: parsed,
        redeemableScope: scope,
        isServiceRecovery,
        reason: reason.trim(),
        expiresAtUtc:
          !isServiceRecovery && expiresAt ? toExpiryInstant(expiresAt) : null,
        clientRequestId: requestId
      });
      toast.success(`U dhanë €${parsed.toFixed(2)} kredite.`);
      handleClose();
      onSuccess?.();
    } catch (err) {
      toast.error(resolveError(err, "Kreditet nuk u dhanë."));
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button onClick={handleOpen}>Dhuro kredite</Button>
      <Modal
        position="center"
        show={show}
        onClose={handleClose}
        hasHeader={false}
        calcHeight={false}
        modalWidth="460px"
        modalHeight="680px"
        buttonElement={
          <Button
            onClick={handleSubmit}
            disabled={submitting || !isEmailValid(email)}
            className="flex items-center"
          >
            {submitting && (
              <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />
            )}
            Dhuro
          </Button>
        }
      >
        <div className="flex justify-center mt-4 relative">
          <h3 className="text-lg font-semibold">Dhuro kredite</h3>
          <IconBtn className="absolute right-2 -top-2" onClick={handleClose}>
            <XMarkIcon className="w-4 h-4 text-neutral" strokeWidth={2} />
          </IconBtn>
        </div>

        <div className="px-6 mt-4 space-y-3">
          <div>
            <label
              htmlFor="wc-email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              id="wc-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              className={`w-full px-3 py-2 border rounded-lg ${
                email.trim() && !isEmailValid(email)
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />
            {email.trim() !== "" && !isEmailValid(email) && (
              <p className="text-xs text-red-500 mt-1">
                Shkruani një email të plotë, p.sh. emri@shembull.com.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="wc-amount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Shuma (€)
            </label>
            <input
              id="wc-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label
              htmlFor="wc-scope"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Ku munden të përdoren
            </label>
            <select
              id="wc-scope"
              value={scope}
              onChange={(e) => setScope(Number(e.target.value))}
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              {Object.entries(REDEEMABLE_SCOPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-start">
            <input
              id="wc-recovery"
              type="checkbox"
              checked={isServiceRecovery}
              onChange={(e) => setIsServiceRecovery(e.target.checked)}
              disabled={submitting}
              className="w-4 h-4 mt-1 border-gray-300 rounded"
            />
            <label
              htmlFor="wc-recovery"
              className="ml-2 text-sm text-gray-700"
            >
              Kompensim për një përvojë të keqe
            </label>
          </div>

          {!isServiceRecovery && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Përdor deri më
              </label>
              {/* onlyModal so the calendar is not clipped by the parent Modal's overflow. */}
              <DayPicker
                onlyModal
                disabled={submitting}
                placeholder="Pas 12 muajsh"
                date={expiresAt}
                pickerProps={{ fromDate: new Date() }}
                onChangeDate={(date) => setExpiresAt(date)}
              />
            </div>
          )}

          <div>
            <label
              htmlFor="wc-reason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Arsyeja <span className="text-red">*</span>
            </label>
            <textarea
              id="wc-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default IssueCreditsModal;
